/**
 * The /dimension-vs-competition "Get a quote" flow's calls into the booking app's
 * Firebase Functions backend (trashwarrior-react, `functions/src/routes/landing.ts`).
 *
 * Why the work happens there and not in a Next.js API route: the Slack notification,
 * the per-agent Calendly API tokens and the `form_drafts` / `lead_assignments` writes
 * all need secrets only the functions runtime holds, while this repo's env is
 * `NEXT_PUBLIC_*` and committed. Same reason `request-ppe-quote.ts` and
 * `request-subscription.ts` are thin fetch wrappers rather than real handlers.
 *
 * ⚠️ PROVISIONAL CONTRACT — `/landing/vs-competition-lead` is still being built in the
 * app repo. Every assumption about its shape is in this file, so settling the contract
 * is a one-file change. The Calendly endpoint below is already in prod
 * (`landing.ts:892`) and only needs the new formId added to
 * `DISPOSAL_CALENDLY_BY_FORM_ID`.
 */
import { getAttribution } from '@src/functions/attribution'
import { getGclid } from '@src/functions/gclid'

export const VS_COMPETITION_FORM_ID = 'dimension-vs-competition'

export const VS_COMPETITION_PATHNAME = '/dimension-vs-competition'

/**
 * Mirrors `DISPOSAL_CALENDLY_BY_FORM_ID["dimension-vs-competition"]`. Used whenever the
 * backend cannot be reached so that a visitor always reaches a calendar — the booking
 * itself is the conversion, and Calendly captures the contact details either way.
 *
 * A reachable backend answers with the same link in production and with
 * `CALENDLY_DEV_LINK` everywhere else, so this constant only matters when the call fails.
 */
export const FALLBACK_AGENT = 'nicholas'
export const FALLBACK_CALENDLY_LINK =
  'https://calendly.com/nicholas-getdimension/introductory-chat-with-dimension'

export type VsCompetitionLead = {
  fullName: string
  companyName: string
  phone_number: string
  email: string
  description: string
}

export type VsCompetitionLeadResult = {
  /** False when the lead was not recorded — the Calendly step still opens. */
  success: boolean
  /** Null on failure; `notifyCalendlyScheduled` needs it and skips the call without it. */
  formDraftId: string | null
  agent: string
  link: string
}

const endpoint = (path: string) => `${process.env.NEXT_PUBLIC_REDIRECT_URL}api/landing/${path}`

const post = async (path: string, body: Record<string, unknown>) => {
  const response = await fetch(endpoint(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  return response.json()
}

/**
 * Records the lead and resolves which agent's calendar to show.
 *
 * Never rejects. A failed submit still returns the fallback calendar rather than
 * stranding the visitor: a booking that exists only in Calendly is worth more than a
 * dead end, and Nick is emailed by Calendly regardless. The cost is the Slack ping and
 * the `lead_assignments` record, both of which need a `formDraftId`.
 */
export const submitVsCompetitionLead = async (
  lead: VsCompetitionLead,
): Promise<VsCompetitionLeadResult> => {
  const attribution = getAttribution()

  const body = {
    ...lead,
    ...attribution,
    // The URL param is `keyword`; the endpoint reads `keywords`, matching the field name
    // on form_drafts. Without this line the keyword is dropped on the way in.
    keywords: attribution.keyword ?? null,
    formId: VS_COMPETITION_FORM_ID,
    pathname: VS_COMPETITION_PATHNAME,
    url: typeof window === 'undefined' ? '' : window.location.href,
    gclId: getGclid(),
  }

  try {
    const data = await post('vs-competition-lead', body)

    if (!data?.success) throw new Error(data?.error ?? 'Lead was not recorded')

    return {
      success: true,
      formDraftId: data.formDraftId ?? null,
      agent: data.agent ?? FALLBACK_AGENT,
      link: data.link ?? FALLBACK_CALENDLY_LINK,
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Could not submit the vs-competition lead', error)

    return {
      success: false,
      formDraftId: null,
      agent: FALLBACK_AGENT,
      link: FALLBACK_CALENDLY_LINK,
    }
  }
}

/**
 * Tells the backend the visitor booked, which is what writes `calendlyEventId` to the
 * form draft, creates the `lead_assignments` doc and fires the Slack message.
 *
 * The solar buyer modal this flow is modelled on never makes this call — it renders the
 * widget and stops listening — which is why solar bookings produce no Slack ping today.
 */
export const notifyCalendlyScheduled = async (payload: {
  formDraftId: string | null
  eventId: string | null
  agent: string
}): Promise<boolean> => {
  if (!payload.formDraftId) {
    // eslint-disable-next-line no-console
    console.error('Calendly booking not reported: the lead submit returned no formDraftId')
    return false
  }

  try {
    const data = await post('calendly-scheduled', {
      formId: VS_COMPETITION_FORM_ID,
      formDraftId: payload.formDraftId,
      eventId: payload.eventId,
      agent: payload.agent,
    })

    return Boolean(data?.success)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Could not report the Calendly booking', error)
    return false
  }
}
